import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { Capacitor } from "@capacitor/core";
import { InAppBrowser } from "@capacitor/inappbrowser";
import { App as CapApp } from "@capacitor/app";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset, info }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  useEffect(() => {
    // Permanent error logger — captures component stack for diagnostic
    // purposes. Uses console.error (always enabled, never gated by debug
    // flag) since this is a real failure path.
    console.error("[ErrorBoundary] Render error caught:", {
      message: (error as Error | undefined)?.message,
      stack: (error as Error | undefined)?.stack,
      componentStack:
        (info as { componentStack?: string } | undefined)?.componentStack ??
        (error as { componentStack?: string } | undefined)?.componentStack,
      timestamp: new Date().toISOString(),
    });
  }, [error, info]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Like2Fit" },
      { name: "google-site-verification", content: "1gCLaoiRvl4CPwkkpmiPB4fhgJAZZ_RcRDrnImdBhZw" },
      { name: "theme-color", content: "#FF5C5C" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Like2Fit" },
    ],
    scripts: [
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-NTQSQCDD67",
        async: true,
      },
      {
        type: "text/javascript",
        children: `window.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag('js', new Date());\ngtag('config', 'G-NTQSQCDD67');`,
      },
      {
        src: "https://s.skimresources.com/js/303154X1791194.skimlinks.js",
        type: "text/javascript",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Like2Fit",
          url: "https://like2fit.com",
          logo: "https://www.like2fit.com/og-image.jpg",
          description: "AI-powered fashion discovery — upload any outfit screenshot and instantly shop every piece.",
        }),
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700;800&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@3/dist/tabler-icons.min.css",
      },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "apple-touch-icon", href: "/icons/icon-192.png", sizes: "192x192" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-MDRCHFND');`,
          }}
        />
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-MDRCHFND"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isInIframe = (() => {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    })();

    const isPreviewHost =
      window.location.hostname.includes("id-preview--") ||
      window.location.hostname.includes("lovableproject.com") ||
      window.location.hostname.includes("lovable.app");

    // Service worker fully removed — product is moving to a Capacitor native
    // app, which handles offline/caching/install natively. Always unregister
    // any existing SW and clear its caches on every host (including production),
    // and never register a new one.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((r) => r.unregister());
      });
      if (typeof caches !== "undefined") {
        caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
      }
    }
  }, []);

  // Native-only: intercept external link taps and open the URL via the
  // Capacitor Browser plugin (iOS SFSafariViewController / Android Chrome
  // Custom Tab) instead of letting the WebView handle the click.
  //
  // PQ-5e-fix-2: previously this used `window.open(href, "_blank")` to hand
  // the URL off to the system default browser. In Capacitor's WKWebView,
  // window.open with `_blank` *also* triggers a same-frame navigation —
  // `createWebViewWith` cannot create a real new window on mobile, so the
  // request falls through and loads in the current WebView. The user saw
  // Safari open AND the in-app view change at the same time. Browser.open
  // never navigates the current WebView: it presents modally and returns
  // the user to the unchanged Like2Fit view on dismiss.
  //
  // Bubble phase so Skimlinks' click handler runs first and rewrites a.href
  // to the affiliate URL before we read it. We do NOT stopImmediatePropagation
  // so React onClick siblings (product_clicked analytics, affiliate_clicks
  // insert, activeTier state updates) still run.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest("a");
      if (!a) return;

      const href = a.getAttribute("href") || "";
      if (!href) return;

      if (href.startsWith("#") || href.startsWith("/")) return;
      if (href.startsWith("mailto:") || href.startsWith("tel:")) return;

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin === window.location.origin) return;
      } catch {
        return;
      }

      // Suppress the anchor's default in-frame navigation and stop bubbling
      // so no router-level listener picks it up. React onClick siblings on
      // the same element have already fired before this bubble-phase
      // document listener, so analytics/state updates are preserved.
      e.preventDefault();
      e.stopPropagation();

      // openInExternalBrowser hands the URL off to the OS-level default
      // browser (Safari on iOS, Chrome on Android) as a separate app, so
      // Like2Fit stays running in the background and the user returns via
      // the app switcher. NOT an in-app browser overlay.
      InAppBrowser.openInExternalBrowser({ url: a.href }).catch((err: unknown) => {
        console.error("[ExternalLink] openInExternalBrowser failed:", err);
      });
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // PHASE-0: Universal link handler. When the OS opens the app via an
  // applinks/App Links URL (e.g. tapped from an email CTA), parse the URL
  // and translate it into an in-app navigation. Web users hit the
  // canonical /?outfit=... route directly; native users land here.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let sub: { remove: () => void } | null = null;
    (async () => {
      sub = await CapApp.addListener("appUrlOpen", ({ url }) => {
        try {
          const parsed = new URL(url);
          // Carry the path + query into the in-app SPA shell.
          const target =
            parsed.pathname +
            (parsed.search ? parsed.search : "") +
            (parsed.hash ? parsed.hash : "");
          // Use a normal pushState so the SPA router picks it up without a
          // full reload (which would tear down the WebView state).
          if (typeof window !== "undefined" && target && target !== "/") {
            window.history.pushState({}, "", target);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }
        } catch (err) {
          console.error("[appUrlOpen] failed to parse", url, err);
        }
      });
    })();
    return () => {
      sub?.remove();
    };
  }, []);

  // Phase 4b: initialise push token registration on native platforms.
  // Lazy-imports the plugin and binds listeners. Idempotent via internal
  // `initialised` flag — safe to call multiple times. The 'registration'
  // listener checks auth state when the token arrives, so listeners
  // bound once correctly route tokens to authed vs guest RPCs across
  // subsequent sign-in / sign-out transitions without re-binding.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    import("@/lib/pushTokens")
      .then(({ initPushTokens }) => initPushTokens())
      .catch((err) => {
        console.error("[__root] initPushTokens import failed", err);
      });
  }, []);

  // Wave 5 Phase 3a — RevenueCat SDK bootstrap. No-op on web + when
  // REVENUECAT_ENABLED flag is false. Owns its own auth listener.
  useEffect(() => {
    import("@/lib/subscription")
      .then(({ initRevenueCat }) => initRevenueCat())
      .catch((err) => {
        console.error("[__root] initRevenueCat import failed", err);
      });
  }, []);

  // Wave 5 Phase 2 — global listener for quota 402 responses. invokeGenerator
  // dispatches l2f:paywall-required when a gated generator returns 402 (or
  // when ?force_paywall=1 simulates it). Single listener, singleton by design
  // like onAuthStateChange — do not add per-route subscribers.
  const router = useRouter();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPaywall = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        generator?: string;
        source?: string;
        outfit_count?: number;
        quota?: number;
      } | undefined;
      router.navigate({
        to: "/paywall",
        search: {
          generator: detail?.generator ?? "unknown",
          source: detail?.source ?? "quota_402",
          outfit_count: Number(detail?.outfit_count ?? 0),
          quota: Number(detail?.quota ?? 5),
        } as never,
      });
    };
    window.addEventListener("l2f:paywall-required", onPaywall);
    return () => window.removeEventListener("l2f:paywall-required", onPaywall);
  }, [router]);




  return (
    <QueryClientProvider client={queryClient}>
      <main>
        <Outlet />
      </main>
      <Toaster />
    </QueryClientProvider>
  );
}
